import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, AlertCircle, CheckCircle2, RotateCcw, ArrowRight, FileSpreadsheet, Upload } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, ShimmerButton, SimHeader } from "@/components/sim/shared";
import { apiCall, ApiError } from "@/lib/api";

interface SimQuestion {
  question_key: string;
  type: string;
  title?: string;
  question: string;
  consequence?: string;
  option_a?: string; option_b?: string; option_c?: string; option_d?: string;
  score_a?: number; score_b?: number; score_c?: number; score_d?: number;
  correct?: string;
  evaluator_prompt?: string;
  min_text_length?: number;
  next_high?: string; next_medium?: string; next_low?: string;
}

interface SpineResponse {
  questions: SimQuestion[];
  coachingPersona: string;
  storyArc: string;
  introText: string;
  branchPointKeys: string[];
  count: number;
  /** Belt-and-suspenders: true when this chunk has any MC whose best
   *  option is the longest by >4 words (a pickable length tell). */
  lengthTell?: boolean;
}

interface VariantResponse {
  questions: SimQuestion[];
  branchKey: string;
  count: number;
  lengthTell?: boolean;
}

interface FormState {
  title: string;
  topic: string;
  audience: string;
  competency: string;
  great: string;
  poor: string;
  characters: string;
  tone: string;
  special: string;
  learningOutcomes: string;
  connectedStory: boolean;
  questionCount: number;
  branchPoints: number;
  mcPercent: number;
  scenarioLength: "crisp" | "standard" | "detailed";
  difficulty: "easy" | "medium" | "hard" | "mixed";
}

const INITIAL_FORM: FormState = {
  title: "",
  topic: "",
  audience: "",
  competency: "",
  great: "",
  poor: "",
  characters: "",
  tone: "",
  special: "",
  learningOutcomes: "",
  connectedStory: true,
  questionCount: 8,
  branchPoints: 1,
  mcPercent: 60,
  scenarioLength: "standard",
  difficulty: "mixed",
};

type Phase = "form" | "generating" | "preview" | "importing" | "done" | "error";
type Mode = "ai" | "excel";

interface Progress {
  step: string;
  done: number;
  total: number;
}

/**
 * AdminBuilderPage — super_admin-only AI Sim Builder. Mirrors NeuArena's
 * proven chunked-generation flow:
 *   1. Loop chunkStart = 1, 5, 9... calling /admin/sim/ai-generate-sim until
 *      all spine decisions are generated. First chunk also returns storyArc +
 *      introText + coachingPersona.
 *   2. For each branch point in branchPointKeys, call ai-generate-variants
 *      in parallel.
 *   3. Combine all questions into one preview.
 *   4. User reviews, clicks Import — POSTs the whole preview to
 *      /admin/sim/ai-import-sim which creates the sim + nodes + choices +
 *      outcome_bands + settings in one transaction.
 */
export function AdminBuilderPage(): JSX.Element {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("ai");
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [phase, setPhase] = useState<Phase>("form");
  const [progress, setProgress] = useState<Progress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    questions: SimQuestion[];
    storyArc: string;
    introText: string;
    coachingPersona: string;
    branchPointKeys: string[];
    /** True if any of the AI's generated batches tripped the length-tell
     *  validator. Admin gets a yellow warning banner above the preview. */
    lengthTell: boolean;
  } | null>(null);
  const [createdSim, setCreatedSim] = useState<{ id: string; simCode: string; title: string } | null>(null);
  const [excelFile, setExcelFile] = useState<File | null>(null);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleGenerate = async (): Promise<void> => {
    if (form.topic.trim().length < 3) {
      setError("Topic is required (min 3 chars).");
      return;
    }
    if (form.title.trim().length < 3) {
      setError("Title is required (min 3 chars).");
      return;
    }
    setError(null);
    setPhase("generating");

    try {
      const N = form.questionCount;
      const CHUNK_SIZE = 4;
      const chunkCount = Math.ceil(N / CHUNK_SIZE);
      let allQuestions: SimQuestion[] = [];
      let storyArc = "";
      let introText = "";
      let coachingPersona = "";
      let bpKeys: string[] = [];
      let anyLengthTell = false;

      // Phase 1 — chunked spine generation.
      for (let chunkIdx = 0; chunkIdx < chunkCount; chunkIdx++) {
        const chunkStart = chunkIdx * CHUNK_SIZE + 1;
        setProgress({ step: `Generating decisions ${chunkStart}-${Math.min(N, chunkStart + CHUNK_SIZE - 1)}`, done: chunkIdx, total: chunkCount + (form.branchPoints > 0 ? 1 : 0) });
        const res = await apiCall<SpineResponse>("/aiGenerateSim", {
          method: "POST",
          timeoutMs: 150_000,
          body: { ...form, chunkStart },
        });
        allQuestions = [...allQuestions, ...res.questions];
        if (res.lengthTell === true) anyLengthTell = true;
        if (chunkIdx === 0) {
          storyArc = res.storyArc;
          introText = res.introText;
          coachingPersona = res.coachingPersona;
          bpKeys = res.branchPointKeys;
        }
      }

      // Phase 2 — branch variants (parallel).
      if (bpKeys.length > 0) {
        setProgress({ step: `Generating branch variants (${bpKeys.length} branch points)`, done: chunkCount, total: chunkCount + 1 });
        const variantPromises = bpKeys.map((bk) => {
          const bpQ = allQuestions.find((q) => q.question_key === bk);
          if (bpQ === undefined) return Promise.resolve<VariantResponse | null>(null);
          // Variant nextKey is the spine question that follows this BP.
          const m = /^d(\d+)$/.exec(bk);
          const nextN = m ? Number(m[1]) + 1 : 0;
          const nextKey = `d${nextN}`;
          return apiCall<VariantResponse>("/aiGenerateVariants", {
            method: "POST",
            timeoutMs: 90_000,
            body: {
              branchKey: bk,
              nextKey,
              bpQuestion: bpQ.question,
              bpTitle: bpQ.title,
              storyArc,
              competency: form.competency,
              tone: form.tone,
              audience: form.audience,
              scenarioLength: form.scenarioLength,
            },
          }).catch((e: unknown) => {
            console.error(`Variant generation failed for ${bk}:`, e);
            return null as VariantResponse | null;
          });
        });
        const variantResults = await Promise.all(variantPromises);
        for (const vr of variantResults) {
          if (vr !== null) {
            allQuestions = [...allQuestions, ...vr.questions];
            if (vr.lengthTell === true) anyLengthTell = true;
          }
        }
      }

      setPreview({ questions: allQuestions, storyArc, introText, coachingPersona, branchPointKeys: bpKeys, lengthTell: anyLengthTell });
      setProgress(null);
      setPhase("preview");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Generation failed";
      setError(msg);
      setProgress(null);
      setPhase("error");
    }
  };

  const handleImport = async (): Promise<void> => {
    if (preview === null) return;
    setPhase("importing");
    setError(null);
    try {
      const res = await apiCall<{ id: string; simCode: string; title: string; nodeCount: number }>(
        "/aiImportSim",
        {
          method: "POST",
          timeoutMs: 60_000,
          body: {
            title: form.title.trim(),
            audience: form.audience.trim() || undefined,
            difficulty: form.difficulty,
            competencyName: form.competency.trim() || undefined,
            storyArc: preview.storyArc,
            introText: preview.introText,
            coachingPersona: preview.coachingPersona,
            questions: preview.questions,
          },
        },
      );
      setCreatedSim(res);
      setPhase("done");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Import failed";
      setError(msg);
      setPhase("error");
    }
  };

  const handleReset = (): void => {
    setPhase("form");
    setPreview(null);
    setProgress(null);
    setError(null);
    setCreatedSim(null);
    setExcelFile(null);
  };

  const handleExcelUpload = async (): Promise<void> => {
    if (excelFile === null) {
      setError("Choose an .xlsx file first.");
      return;
    }
    setError(null);
    setPhase("importing");
    try {
      // Read file → base64. Avoids multipart parsing on the Functions side.
      const buf = await excelFile.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = "";
      for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i] as number);
      const fileBase64 = btoa(binary);

      const res = await apiCall<{ id: string; simCode: string; title: string; nodeCount: number }>(
        "/excelImportSim",
        { method: "POST", timeoutMs: 90_000, body: { fileBase64 } },
      );
      setCreatedSim(res);
      setPhase("done");
    } catch (err) {
      // ApiError.message is the API's `error` field; some endpoints (excelImportSim)
      // also include a `detail` we want to show.
      let msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Excel import failed";
      if (err instanceof ApiError && err.detail !== undefined) msg = `${msg} — ${err.detail}`;
      setError(msg);
      setPhase("error");
    }
  };

  return (
    <ScreenShell>
      <SimHeader decision={0} xp={0} />
      <main className="mx-auto max-w-4xl px-4 pb-20 md:px-8">
        <button
          type="button"
          onClick={() => navigate("/simulations")}
          className="mb-5 inline-flex items-center gap-1.5 text-xs text-white/50 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to simulations
        </button>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 text-fuchsia-300">
            <Sparkles className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]">AI Sim Builder</span>
          </div>
          <h1 className="mt-2 text-[28px] font-bold leading-tight">
            <GradientText>Author a new simulation</GradientText>
          </h1>
          <p className="mt-1 text-sm text-white/55">
            Two paths: let Claude generate the full simulation from a brief, or upload a SharePoint-format Excel template.
          </p>
        </motion.div>

        {/* ── TAB SWITCHER ── */}
        {(phase === "form" || phase === "error") && createdSim === null && (
          <div className="mt-5 flex gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-1">
            <button
              type="button"
              onClick={() => { setMode("ai"); setError(null); setPhase("form"); }}
              className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                mode === "ai" ? "bg-fuchsia-500/20 text-white" : "text-white/55 hover:text-white"
              }`}
            >
              <Sparkles className="mr-1.5 inline h-3.5 w-3.5" /> AI Builder
            </button>
            <button
              type="button"
              onClick={() => { setMode("excel"); setError(null); setPhase("form"); }}
              className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                mode === "excel" ? "bg-cyan-500/20 text-white" : "text-white/55 hover:text-white"
              }`}
            >
              <FileSpreadsheet className="mr-1.5 inline h-3.5 w-3.5" /> Excel Upload
            </button>
          </div>
        )}

        {/* ── EXCEL UPLOAD PANEL ── */}
        {mode === "excel" && phase === "form" && (
          <GlassCard className="mt-6 p-6 md:p-8">
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold">Upload a SharePoint-format Excel</h2>
                <p className="mt-1 text-sm text-white/55">
                  The file must contain at least these sheets: <span className="font-mono text-[12px] text-white/75">Simulations</span>,{" "}
                  <span className="font-mono text-[12px] text-white/75">SimulationNodes</span>,{" "}
                  <span className="font-mono text-[12px] text-white/75">SimulationChoices</span>. Optional:{" "}
                  <span className="font-mono text-[12px] text-white/75">SimulationSettings</span>,{" "}
                  <span className="font-mono text-[12px] text-white/75">OutcomeBands</span>. The sim_code is auto-generated as the next SIM&lt;NNNN&gt; — the file&rsquo;s SimulationID column is ignored.
                </p>
              </div>

              <div className="rounded-xl border border-dashed border-white/20 bg-white/[0.02] p-8 text-center">
                <FileSpreadsheet className="mx-auto h-10 w-10 text-cyan-300" />
                <p className="mt-3 text-sm text-white/70">
                  {excelFile !== null
                    ? <span><span className="font-medium text-white">{excelFile.name}</span> · {(excelFile.size / 1024).toFixed(1)} KB</span>
                    : "Pick a .xlsx file to upload"}
                </p>
                <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10">
                  <Upload className="h-4 w-4" />
                  {excelFile !== null ? "Choose another file" : "Choose file"}
                  <input
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setExcelFile(e.target.files?.[0] ?? null)}
                    className="hidden"
                  />
                </label>
              </div>

              {error !== null && (
                <p className="rounded-md border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p>
              )}

              <div className="flex justify-end pt-2">
                <ShimmerButton onClick={() => void handleExcelUpload()}>
                  <Upload className="h-4 w-4" /> Upload &amp; Import
                </ShimmerButton>
              </div>
            </div>
          </GlassCard>
        )}

        {/* ── FORM ── */}
        {mode === "ai" && phase === "form" && (
          <GlassCard className="mt-6 p-6 md:p-8">
            <div className="space-y-5">
              <Field label="Simulation title *" hint="Shown on the simulations list">
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  placeholder="e.g. Leading Through Crisis"
                  maxLength={200}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                />
              </Field>

              <Field label="Topic / situation *" hint="The leadership challenge at the heart of this sim">
                <textarea
                  value={form.topic}
                  onChange={(e) => update("topic", e.target.value)}
                  placeholder="e.g. A pharma CRO must triple operational capacity in 18 months to stay on top clients' vendor panels..."
                  rows={4}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                />
              </Field>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field label="Audience" hint="Who the learners are">
                  <input
                    type="text"
                    value={form.audience}
                    onChange={(e) => update("audience", e.target.value)}
                    placeholder="e.g. Department heads in Indian organisations"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                  />
                </Field>
                <Field label="Competency being developed" hint="Title + 1-line definition">
                  <input
                    type="text"
                    value={form.competency}
                    onChange={(e) => update("competency", e.target.value)}
                    placeholder="e.g. Leading transformation"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field label="What GREAT looks like (optional)">
                  <textarea
                    value={form.great}
                    onChange={(e) => update("great", e.target.value)}
                    placeholder="The behaviour you want to reward"
                    rows={3}
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                  />
                </Field>
                <Field label="What POOR looks like (optional)">
                  <textarea
                    value={form.poor}
                    onChange={(e) => update("poor", e.target.value)}
                    placeholder="The derailers you want to catch"
                    rows={3}
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                  />
                </Field>
              </div>

              <Field label="Characters / details to weave in (optional)" hint="Names, roles, specific tensions">
                <textarea
                  value={form.characters}
                  onChange={(e) => update("characters", e.target.value)}
                  placeholder="e.g. Priya (Head of Strategy), Rahul (CFO, sceptic)..."
                  rows={2}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                />
              </Field>

              <Field label="Story structure" hint="One connected arc, or independent vignettes">
                <div className="flex gap-2">
                  {([
                    { v: true, label: "Connected story", hint: "One cast, escalating arc" },
                    { v: false, label: "Independent decisions", hint: "Separate situations" },
                  ] as const).map((opt) => {
                    const selected = form.connectedStory === opt.v;
                    return (
                      <button
                        key={String(opt.v)}
                        type="button"
                        onClick={() => update("connectedStory", opt.v)}
                        className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                          selected
                            ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-white"
                            : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/25 hover:text-white/80"
                        }`}
                      >
                        <div className="text-sm font-semibold">{opt.label}</div>
                        <div className="text-[10px] opacity-70">{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Tone of scenarios (optional)" hint="The emotional register">
                <input
                  type="text"
                  value={form.tone}
                  onChange={(e) => update("tone", e.target.value)}
                  placeholder="e.g. High-pressure but respectful"
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                />
              </Field>

              <Field label="Special instructions / focus / avoid (optional)">
                <textarea
                  value={form.special}
                  onChange={(e) => update("special", e.target.value)}
                  placeholder="e.g. Include a missed-deadline-by-a-star-performer moment; avoid layoffs as a theme"
                  rows={2}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                />
              </Field>

              <Field label="Key learning outcomes (optional)" hint="Improves coaching quality">
                <textarea
                  value={form.learningOutcomes}
                  onChange={(e) => update("learningOutcomes", e.target.value)}
                  placeholder={"e.g. Participants will be able to:\n- Give specific, behaviour-focused feedback without triggering defensiveness\n- Distinguish between feedback and evaluation"}
                  rows={3}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm leading-relaxed text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                <NumberField label="Decisions" value={form.questionCount} onChange={(v) => update("questionCount", v)} min={4} max={25} />
                <NumberField label="Branch points" value={form.branchPoints} onChange={(v) => update("branchPoints", v)} min={0} max={3} />
                <NumberField label="MC %" value={form.mcPercent} onChange={(v) => update("mcPercent", v)} min={0} max={100} />
                <SelectField label="Scenario length" value={form.scenarioLength} onChange={(v) => update("scenarioLength", v as FormState["scenarioLength"])} options={["crisp", "standard", "detailed"]} />
                <SelectField label="Difficulty" value={form.difficulty} onChange={(v) => update("difficulty", v as FormState["difficulty"])} options={["easy", "medium", "hard", "mixed"]} />
              </div>

              {error !== null && (
                <p className="rounded-md border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p>
              )}

              <div className="flex justify-end pt-2">
                <ShimmerButton onClick={() => void handleGenerate()}>
                  <Sparkles className="h-4 w-4" /> Generate simulation
                </ShimmerButton>
              </div>
            </div>
          </GlassCard>
        )}

        {/* ── GENERATING ── */}
        {phase === "generating" && (
          <GlassCard className="mt-6 p-10 text-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} className="mx-auto h-12 w-12 rounded-full border-2 border-white/10 border-t-fuchsia-400" />
            <p className="mt-5 bg-gradient-to-r from-fuchsia-300 via-violet-200 to-cyan-300 bg-clip-text text-sm font-medium text-transparent">
              {progress?.step ?? "Generating…"}
            </p>
            {progress !== null && (
              <p className="mt-2 text-[11px] text-white/40">Step {progress.done + 1} of {progress.total}</p>
            )}
            <p className="mt-4 text-[11px] text-white/35">Chunked spine generation + parallel branch variants. Usually 30–90 seconds total.</p>
          </GlassCard>
        )}

        {/* ── ERROR ── */}
        {phase === "error" && (
          <GlassCard className="mt-6 p-10 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-[#f87272]" />
            <h2 className="mt-3 text-base font-semibold">{mode === "excel" ? "Excel import failed" : "Generation failed"}</h2>
            <p className="mt-1 text-sm text-white/55 whitespace-pre-wrap break-words">{error}</p>
            <div className="mt-5 flex justify-center gap-3">
              <button type="button" onClick={handleReset} className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10">
                <RotateCcw className="h-3.5 w-3.5" /> Start over
              </button>
              <ShimmerButton onClick={() => void (mode === "excel" ? handleExcelUpload() : handleGenerate())}>Try again</ShimmerButton>
            </div>
          </GlassCard>
        )}

        {/* ── PREVIEW ── */}
        {phase === "preview" && preview !== null && (
          <div className="mt-6 space-y-5">
            {preview.lengthTell && (
              <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-[12px] text-amber-100">
                <div className="font-semibold">Possible length tell — review before saving</div>
                <p className="mt-1 text-[11.5px] text-amber-100/80">
                  At least one MC item below has its best-scoring option visibly longer than the others (&gt;4 words ahead of the runner-up).
                  Smart learners can pick the answer from length alone. Consider regenerating, or hand-edit the options to match length / tone / detail.
                </p>
              </div>
            )}
            <GlassCard className="p-6">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">Story arc</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/80">{preview.storyArc || "—"}</p>
            </GlassCard>

            <GlassCard className="p-6">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">Welcome briefing</h2>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[12px] leading-relaxed text-white/75">{preview.introText || "—"}</pre>
            </GlassCard>

            <GlassCard className="p-6">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">Coaching persona</h2>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[12px] leading-relaxed text-white/75">{preview.coachingPersona || "—"}</pre>
            </GlassCard>

            <GlassCard className="p-6">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-fuchsia-200">
                Decisions ({preview.questions.length})
              </h2>
              <ul className="mt-3 space-y-2">
                {preview.questions.map((q) => (
                  <li key={q.question_key} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/40">
                          {q.question_key} · {q.type === "scenario" ? "Multiple choice" : "Open text"}
                          {q.title !== undefined && q.title !== "" ? ` · ${q.title}` : ""}
                        </p>
                        <p className="mt-1 text-[13px] leading-snug text-white/85">{q.question}</p>
                      </div>
                      <span className="shrink-0 text-[10px] text-white/30">
                        → {q.next_high ?? "·"} / {q.next_medium ?? "·"} / {q.next_low ?? "·"}
                      </span>
                    </div>
                    {q.type === "scenario" && (
                      <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                        {(["a", "b", "c", "d"] as const).map((l) => {
                          const text = q[("option_" + l) as keyof SimQuestion] as string | undefined;
                          const score = q[("score_" + l) as keyof SimQuestion] as number | undefined;
                          if (text === undefined) return null;
                          return (
                            <div key={l} className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1 text-[11px] text-white/65">
                              <span className="font-bold text-white/40">{l.toUpperCase()}</span> {text}
                              <span className="ml-1 text-white/30">({score ?? 0})</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </GlassCard>

            {error !== null && (
              <p className="rounded-md border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p>
            )}

            <div className="flex justify-between gap-3">
              <button type="button" onClick={handleReset} className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10">
                <RotateCcw className="h-3.5 w-3.5" /> Start over
              </button>
              <ShimmerButton onClick={() => void handleImport()}>
                Import → live sim <ArrowRight className="h-4 w-4" />
              </ShimmerButton>
            </div>
          </div>
        )}

        {/* ── IMPORTING ── */}
        {phase === "importing" && (
          <GlassCard className="mt-6 p-10 text-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} className="mx-auto h-10 w-10 rounded-full border-2 border-white/10 border-t-fuchsia-400" />
            <p className="mt-4 text-sm text-white/60">Saving simulation to the database…</p>
          </GlassCard>
        )}

        {/* ── DONE ── */}
        {phase === "done" && createdSim !== null && (
          <GlassCard className="mt-6 p-10 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#10b981]" />
            <h2 className="mt-3 text-base font-semibold">
              <GradientText>{createdSim.title}</GradientText>
            </h2>
            <p className="mt-1 text-sm text-white/55">Live as <span className="font-mono text-white/80">{createdSim.simCode}</span></p>
            <div className="mt-6 flex justify-center gap-3">
              <button type="button" onClick={handleReset} className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10">
                Author another
              </button>
              <ShimmerButton onClick={() => navigate("/simulations")}>
                See it on the list <ArrowRight className="h-4 w-4" />
              </ShimmerButton>
            </div>
          </GlassCard>
        )}
      </main>
    </ScreenShell>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.12em] text-white/55">{label}</label>
      {hint !== undefined && <span className="ml-2 text-[10px] text-white/30">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function NumberField({ label, value, onChange, min, max }: { label: string; value: number; onChange: (v: number) => void; min: number; max: number }): JSX.Element {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/45">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value))))}
        className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }): JSX.Element {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/45">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-[#0c0e22]">{opt}</option>
        ))}
      </select>
    </div>
  );
}
